import type { PlanName } from './types';

import { PLAN_NAME, PlanType } from '@readme/plans';

import { planTypeToDisplayName } from '@routes/Dash/ManagePlans/PlanSelector/utils';

const upgradePlanData = {
  freelaunch: {
    type: PlanType.FREE,
    enabled: true,
    // Plan names considered "this plan" - if user is on one, show "Current Plan"
    planNames: [PLAN_NAME.FREELAUNCH, PLAN_NAME.FREELAUNCH2026] as readonly PLAN_NAME[],
    title: planTypeToDisplayName[PlanType.FREE],
    description: 'Get started with beautiful public technical docs',
    aiDescription: 'Included AI Features',
    priceMonth: 0,
    priceAnnual: 0,
    features: [
      { icon: 'fa-globe', label: 'Custom Domain' },
      { icon: 'fa-arrows-rotate', label: 'Bidirectional Sync' },
      { icon: 'fa-code-simple', label: 'Interactive API Reference' },
      { icon: 'fa-chart-line', label: 'Usage Metrics' },
      { icon: 'fa-file-pen', label: 'Markdown Editor' },
      { icon: 'fa-brush', label: 'Customizable Themes' },
    ],
    aiFeatures: [
      { icon: 'fa-square-caret-down', label: 'AI Dropdown' },
      { icon: 'fa-book-atlas', label: 'LLMs.txt' },
      { icon: 'fa-server', label: 'MCP Server' },
    ],
  },
  startup: {
    type: PlanType.STARTUP,
    enabled: false, // Legacy plan - no longer sold
    planNames: [
      PLAN_NAME.STARTUP,
      PLAN_NAME.STARTUP_2018,
      PLAN_NAME.STARTUP_ANNUAL_2024,
      PLAN_NAME.OPENSOURCE,
    ] as readonly PLAN_NAME[],
    title: planTypeToDisplayName[PlanType.STARTUP],
    description: 'For teams looking to build out their docs and create a community.',
    aiDescription: 'All Starter AI features, plus…',
    priceMonth: 99,
    priceAnnual: 79, // monthly price for an annual subscription (~20% cheaper)
    features: [
      { icon: 'fa-arrows-rotate', label: 'Bidirectional Sync' },
      { icon: 'fa-megaphone', label: 'Changelog' },
      { icon: 'fa-comment', label: 'Discussion Forum' },
      { icon: 'fa-house', label: 'Landing Page' },
      { icon: 'fa-code', label: 'MDX Components' },
      { icon: 'fa-globe', label: 'Custom Domain' },
    ],
    aiFeatures: [
      { icon: 'fa-feather', label: 'AI Linter' },
      { icon: 'fa-message-bot', label: 'Agent Owlbert (Preview)' },
      { icon: 'fa-brain', label: 'Docs Audit (Preview)' },
    ],
  },
  business: {
    type: PlanType.BUSINESS,
    enabled: false, // Legacy plan - no longer sold
    // Legacy Business plans - no longer sold but still active for existing customers
    planNames: [PLAN_NAME.BUSINESS, PLAN_NAME.BUSINESS_2018, PLAN_NAME.BUSINESS_ANNUAL_2024] as readonly PLAN_NAME[],
    title: planTypeToDisplayName[PlanType.BUSINESS],
    description: 'For teams who want more styling and management tools.',
    upsellDescription:
      "Docs shouldn't slow you down. Everything your team needs to edit, review, and ship docs faster.",
    upsellFeatures: [
      { icon: 'fa-badge-check', label: 'For growing teams that need structured branching and diff views' },
      { icon: 'fa-badge-check', label: 'Fully customizable CSS/HTML to match your brand' },
      { icon: 'fa-badge-check', label: 'Made to scale with reusable content' },
    ],
    aiDescription: 'All Startup AI features, plus…',
    priceMonth: 399,
    priceAnnual: 349,
    features: [
      { icon: 'fa-user-plus', label: 'Invite Teammates' },
      { icon: 'fa-code-branch', label: 'Branching and Reviews' },
      { icon: 'fa-lock', label: 'Private Docs' },
      { icon: 'fa-house', label: 'Landing Page' },
      { icon: 'fa-megaphone', label: 'Changelog, Recipes & Forums' },
      { icon: 'fa-code', label: 'Custom MDX Components' },
      { icon: 'fa-recycle', label: 'Reusable Content' },
      { icon: 'fa-brackets-curly', label: 'CSS/HTML' },
    ],
    aiFeatures: [
      { icon: 'fa-message-bot', label: 'Agent Owlbert' },
      { icon: 'fa-sparkles', label: 'Ask AI Lite (included)' },
      { icon: 'fa-code-pull-request', label: 'AI Writer' },
      { icon: 'fa-brain', label: 'Docs Audit (Preview)' },
    ],
  },
  pro: {
    type: PlanType.PRO,
    enabled: true,
    planNames: [PLAN_NAME.PRO2026, PLAN_NAME.PRO2026_ANNUAL] as readonly PLAN_NAME[],
    title: planTypeToDisplayName[PlanType.PRO],
    description: 'For teams who want more styling and management tools.',
    aiDescription: 'All Startup AI features, plus…',
    priceMonth: 300,
    priceAnnual: 250,
    features: [
      { icon: 'fa-user-plus', label: 'Invite Teammates' },
      { icon: 'fa-code-branch', label: 'Branching and Reviews' },
      { icon: 'fa-lock', label: 'Private Docs' },
      { icon: 'fa-house', label: 'Landing Page' },
      { icon: 'fa-megaphone', label: 'Changelog, Recipes & Forums' },
      { icon: 'fa-code', label: 'Custom MDX Components' },
      { icon: 'fa-recycle', label: 'Reusable Content' },
      { icon: 'fa-brackets-curly', label: 'CSS/HTML' },
    ],
    aiFeatures: [
      { icon: 'fa-message-bot', label: 'Agent Owlbert' },
      { icon: 'fa-sparkles', label: 'Ask AI Lite (included)' },
      { icon: 'fa-code-pull-request', label: 'AI Writer' },
      { icon: 'fa-brain', label: 'Docs Audit (Preview)' },
    ],
  },
  enterprise: {
    type: PlanType.ENTERPRISE,
    enabled: true,
    planNames: [PLAN_NAME.ENTERPRISE] as readonly PLAN_NAME[],
    title: planTypeToDisplayName[PlanType.ENTERPRISE],
    description: 'For organizations looking to build scalable docs with multi-project management and security needs.',
    upsellDescription:
      'Scale with confidence. Manage multiple projects with collaboration, control, and enterprise-level security.',
    upsellFeatures: [
      { icon: 'fa-badge-check', label: 'For organizations that need advanced branching and enforced review workflows' },
      { icon: 'fa-badge-check', label: 'Advanced access control with granular roles and permissions' },
      { icon: 'fa-badge-check', label: 'Enterprise-grade security including SSO, OAuth, and audit logs' },
      { icon: 'fa-badge-check', label: 'Dedicated dev and design help' },
    ],
    aiDescription: 'All Pro AI features, plus…',
    priceMonth: 'Contact Us',
    priceAnnual: 'Contact Us',
    features: [
      { icon: 'fa-layer-group', label: 'Multiple Combined Projects' },
      { icon: 'fa-delete-left', label: 'No ReadMe Branding' },
      { icon: 'fa-users', label: 'User Roles & Access Control' },
      { icon: 'fa-box-taped', label: 'Global Reusable Content Library' },
      { icon: 'fa-list-timeline', label: 'Audit Logs' },
      { icon: 'fa-lock-keyhole', label: 'SSO, OAuth, & More' },
      { icon: 'fa-hand-heart', label: 'Dedicated Support' },
      { icon: 'fa-wrench', label: 'Implementation Services' },
    ],
    aiFeatures: [
      { icon: 'fa-book-sparkles', label: 'Private AI Context' },
      { icon: 'fa-brain', label: 'Docs Audit with History' },
      { icon: 'fa-square-terminal', label: 'Ask AI API' },
    ],
  },
} as const;

type UpgradePlanData = typeof upgradePlanData;
export type UpgradePlanDataValue = UpgradePlanData[PlanName];

export default upgradePlanData;
